// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utility functions for exporting inventory overview data as CSV.
 * Follows the same pattern as other CSV exports in the frontend.
 */
import { InventoryOverviewData } from '@/inventory/components/overview/types';
import {
  buildCSVContent,
  escapeCSV,
  generateCSVFilename,
  triggerCSVDownload,
} from '@/utilities/csv-export';

/**
 * Transforms inventory overview data into CSV format and triggers download.
 *
 * CSV format:
 * - Headers: "Product", "SKU", "Location", "Inventory", "Okay", "Pending", "Error", "Scrapped" (if enabled)
 * - Inventory column: "Yes" for inventory locations, "No" for non-inventory locations
 * - One row per product-location combination
 * - In transit items shown as "IN_TRANSIT" location with "No" inventory
 * - Scrapped column only included when includeScrappedAssets is true
 *
 * @param data - The inventory overview data to export
 * @param includeScrappedAssets - Whether to include the scrapped column in the export
 */
export function exportInventoryOverviewCSV(
  data: InventoryOverviewData,
  includeScrappedAssets = false
): void {
  const headers = [
    'Product',
    'SKU',
    'Location',
    'Inventory',
    'Okay',
    'Pending',
    'Error',
  ];

  if (includeScrappedAssets) {
    headers.push('Scrapped');
  }

  const rows: string[][] = [];

  // Process each product row
  for (const productRow of data.productRows) {
    const productName = productRow.product.name;
    const productSku = productRow.product.sku;

    // Add inventory locations
    for (const location of data.inventoryLocations) {
      const counts = productRow.locationCounts[location.location.key];
      const row = [
        escapeCSV(productName),
        escapeCSV(productSku),
        escapeCSV(location.location.name),
        'Yes',
        counts.FUNCTIONAL.toString(),
        counts.NEEDS_REVIEW.toString(),
        counts['NON_FUNCTIONAL'].toString(),
      ];

      if (includeScrappedAssets) {
        row.push(counts.SCRAPPED.toString());
      }

      rows.push(row);
    }

    // Add non-inventory locations
    for (const location of data.nonInventoryLocations) {
      const counts = productRow.locationCounts[location.location.key];
      const row = [
        escapeCSV(productName),
        escapeCSV(productSku),
        escapeCSV(location.location.name),
        'No',
        counts.FUNCTIONAL.toString(),
        counts.NEEDS_REVIEW.toString(),
        counts['NON_FUNCTIONAL'].toString(),
      ];

      if (includeScrappedAssets) {
        row.push(counts.SCRAPPED.toString());
      }

      rows.push(row);
    }

    // Add in transit items
    const inTransitCounts = productRow.inTransitCounts;
    const inTransitRow = [
      escapeCSV(productName),
      escapeCSV(productSku),
      'IN_TRANSIT',
      'No',
      inTransitCounts.FUNCTIONAL.toString(),
      inTransitCounts.NEEDS_REVIEW.toString(),
      inTransitCounts['NON_FUNCTIONAL'].toString(),
    ];

    if (includeScrappedAssets) {
      inTransitRow.push(inTransitCounts.SCRAPPED.toString());
    }

    rows.push(inTransitRow);
  }

  const csvContent = buildCSVContent(headers, rows);
  triggerCSVDownload(csvContent, generateCSVFilename('inventory-overview'));
}
